import { Injectable } from '@angular/core';
import { OAuthErrorEvent, OAuthEvent, OAuthService } from 'angular-oauth2-oidc';
import { Guid } from 'guid-typescript';
import { BehaviorSubject, Observable } from 'rxjs';

import { DiscoveryDocumentConfig, resetPasswordUrl } from './auth.config';

export interface User {
  readonly userId: Guid;
  readonly isAuthenticated: boolean;
  readonly name: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private currentUserSubject$: BehaviorSubject<User>;
  public currentUser$: Observable<User>;

  private static createInitialUser(): User {
    return {
        userId: Guid.createEmpty(),
        isAuthenticated: false,
        name: undefined
    };
  }

  private static getUser(hasValidAccessToken: boolean, identityClaims: any): User {
    const isAuthenticated = hasValidAccessToken;
    const userId = identityClaims?.sub ?? Guid.createEmpty();
    const name = identityClaims?.name ?? '';

    return { isAuthenticated, userId, name };
  }

  private static isPasswordResetRequest(errorEvent: OAuthErrorEvent): boolean {
    if (errorEvent.type !== 'code_error') {
      return false;
    }
    const params = errorEvent.params as any;
    return params?.error === 'access_denied' &&
      (params.error_description as string).startsWith('AADB2C90118');
  }

  constructor(private internalAuthService: OAuthService) {
    const initialUser = AuthService.createInitialUser();
    this.currentUserSubject$ = new BehaviorSubject<User>(initialUser);
    this.currentUser$ = this.currentUserSubject$.asObservable();
    this.internalAuthService.events.subscribe(event => this.handleOAuthEvent(event));
  }

  public init(): Promise<void> {
    return this.internalAuthService.loadDiscoveryDocument(DiscoveryDocumentConfig.url)
      .then(() => this.internalAuthService.setupAutomaticSilentRefresh())
      .then(() => this.internalAuthService.tryLoginCodeFlow())
      .then(() => this.setCurrentUser());
  }

  public login(): void {
    this.internalAuthService.initLoginFlow();
  }

  public logout(): void {
    this.internalAuthService.logOut();
  }

  public getCurrentUser(): User {
    return this.currentUserSubject$.value;
  }

  private handleOAuthEvent(event: OAuthEvent): void {
    if (event instanceof OAuthErrorEvent) {
      console.error('OAuth error event occured:', event);
      if (AuthService.isPasswordResetRequest(event)) {
        window.location.href = resetPasswordUrl;
      }
    } else {
      console.log('OAuth event occured:', event);
    }

    this.setCurrentUser();
  }

  private setCurrentUser(): void {
    const currentUser = AuthService.getUser(
      this.internalAuthService.hasValidAccessToken(),
      this.internalAuthService.getIdentityClaims());

    this.currentUserSubject$.next(currentUser);
  }
}
